import type { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import type { SpotDefaultConfig } from '../../api/tradingTypes'
import { SPOTBOT_PUBLIC_IP } from '../../config'

/** Card-header tab strip shared by legacy `v3-settings-default`,
 * `v3-settings-exchange` and `service-account-list` templates. */
export function SettingsTabs() {
  const { pathname } = useLocation()
  const tabs = [
    { to: '/settings/default', label: 'Default coin config' },
    { to: '/settings/exchange', label: 'Exchange' },
    { to: '/settings/service-accounts', label: 'Service Accounts' },
  ]
  return (
    <nav className="portal-tabs" aria-label="Settings sections">
      {tabs.map((tab) => (
        <Link
          key={tab.to}
          to={tab.to}
          className={pathname === tab.to ? 'portal-tab portal-tab--active' : 'portal-tab'}
          aria-current={pathname === tab.to ? 'page' : undefined}
        >
          {tab.label}
        </Link>
      ))}
    </nav>
  )
}

/** Legacy `alert-info` box on both settings pages: the address users must
 * whitelist on their exchange API key. It is the address the SPOTBOT places
 * orders from — spotbot still runs on GCP in round 1, so the legacy value
 * stays correct until spotbot itself moves (see src/config.ts). */
export function PublicIpNotice() {
  return (
    <p className="portal-notice portal-notice--info">
      GrabbitBot public IP address: <strong>{SPOTBOT_PUBLIC_IP}</strong>
    </p>
  )
}

/** Legacy `row bg-warning` "You haven't started any BOTs yet" alert. */
export function NoBotsNotice({ withLink }: { withLink?: boolean }) {
  return (
    <p className="portal-notice portal-notice--warning" role="status">
      <strong>You haven&apos;t started any BOTs yet</strong>
      {withLink && (
        <>
          <br />
          Please go to <Link to="/settings/default">Settings / Default coin config</Link> to start your first BOT.
        </>
      )}
    </p>
  )
}

/** Exchange-picker tab strip for the settings pages (one tab per configured
 * exchange, disabled while a save is running — legacy rendered a second,
 * `disabled` copy of the strip for that state). */
export function ExchangeTabs({
  configs,
  exchangeId,
  onSelect,
  disabled,
  renderSuffix,
}: {
  configs: SpotDefaultConfig[]
  exchangeId: string
  onSelect: (exchangeId: string) => void
  disabled?: boolean
  renderSuffix?: (config: SpotDefaultConfig) => ReactNode
}) {
  return (
    <div className="portal-tabs portal-tabs--sub" role="tablist" aria-label="Exchange">
      {configs.map((config) => (
        <button
          key={config.exchangeId}
          type="button"
          role="tab"
          aria-selected={config.exchangeId === exchangeId}
          className={config.exchangeId === exchangeId ? 'portal-tab portal-tab--active' : 'portal-tab'}
          disabled={disabled}
          onClick={() => onSelect(config.exchangeId)}
        >
          {config.exchangeId}
          {renderSuffix?.(config)}
        </button>
      ))}
    </div>
  )
}

